import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  LayoutDashboard,
  Users,
  Building2,
  Store,
  CreditCard,
  ScrollText,
  LogOut,
  Menu,
  X,
  Shield,
} from "lucide-react";
import { logoutSuperAdmin } from "../../slices/superAdminAuthSlice";
import type { AppDispatch } from "../../store";

const navItems = [
  { path: "/superadmin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/superadmin/users", label: "Users", icon: Users },
  { path: "/superadmin/businesses", label: "Businesses", icon: Building2 },
  { path: "/superadmin/stores", label: "Stores", icon: Store },
  { path: "/superadmin/subscriptions", label: "Subscriptions", icon: CreditCard },
  { path: "/superadmin/audit-log", label: "Audit Log", icon: ScrollText },
];

interface Props {
  children: React.ReactNode;
}

const SuperAdminLayout = ({ children }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await dispatch(logoutSuperAdmin());
    navigate("/superadmin/login");
  };

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-gray-900 text-white w-64">
      <div className="flex items-center gap-2 px-6 py-5 border-b border-gray-700">
        <Shield className="w-6 h-6 text-indigo-400" />
        <span className="font-bold text-lg tracking-tight">SuperAdmin</span>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? "bg-indigo-600 text-white"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </Link>
          );
        })}
      </nav>
      <div className="px-3 pb-6">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="relative flex flex-col w-64 flex-shrink-0">
            <Sidebar />
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 md:hidden">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            <span className="font-bold text-gray-900">SuperAdmin</span>
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1 rounded-md text-gray-500">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
